import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addInvoiceLine,
  cancelInvoice,
  createFinanceRefund,
  createInvoice,
  fetchFinanceInvoiceCreators,
  fetchFinanceInvoices,
  fetchFinanceRefunds,
  fetchGroupInvoiceCreators,
  fetchGroupTransactions,
  fetchInvoice,
  fetchInvoiceEvents,
  fetchInvoiceReceipts,
  fetchInvoices,
  fetchOrgTransactions,
  fetchRefundablePayments,
  finalizeInvoice,
  type InvoiceListQuery,
  provisionInvoice,
  type RefundListQuery,
  recordInvoicePayment,
  recordInvoicePayout,
  removeInvoiceLine,
  reverseFinanceRefund,
  reverseInvoiceReceipt,
  type UpdateInvoicePayload,
  updateInvoice,
  voidInvoice,
} from "@/api/invoices";
import type {
  AddLinePayload,
  CreateInvoicePayload,
  CreateRefundPayload,
  ReceiptListQuery,
  RecordPaymentPayload,
  ReverseLotAction,
  ReverseRefundPayload,
} from "@/interfaces/invoices";
import type { RecordPayoutPayload } from "@/interfaces/purchases";

export const invoicesKey = (groupId: number) => ["invoices", groupId] as const;
export const invoiceKey = (groupId: number, invoiceId: number) =>
  ["invoice", groupId, invoiceId] as const;
export const invoiceEventsKey = (groupId: number, invoiceId: number) =>
  ["invoiceEvents", groupId, invoiceId] as const;
export const invoiceReceiptsKey = (groupId: number, invoiceId: number) =>
  ["invoiceReceipts", groupId, invoiceId] as const;
export const groupTransactionsKey = (groupId: number) => ["groupTransactions", groupId] as const;
export const financeInvoicesKey = (query?: InvoiceListQuery) =>
  ["financeInvoices", query ?? {}] as const;

/** The group's invoices — server-paginated (blotter). */
export function useInvoices(groupId: number | null | undefined, query: InvoiceListQuery = {}) {
  return useQuery({
    queryKey: ["invoices", groupId, query],
    queryFn: () => fetchInvoices(groupId as number, query),
    enabled: groupId != null,
  });
}

/** One invoice with its lines. */
export function useInvoice(groupId: number, invoiceId: number | null) {
  return useQuery({
    queryKey: ["invoice", groupId, invoiceId],
    queryFn: () => fetchInvoice(groupId, invoiceId as number),
    enabled: invoiceId != null,
  });
}

/** Refreshes the list + a single invoice + its activity log + the finance blotter + the wallet
 *  (a paid invoice mints credits). */
function useInvoiceInvalidators(groupId: number) {
  const qc = useQueryClient();
  return (invoiceId?: number) => {
    qc.invalidateQueries({ queryKey: invoicesKey(groupId) });
    qc.invalidateQueries({ queryKey: ["financeInvoices"] });
    qc.invalidateQueries({ queryKey: groupTransactionsKey(groupId) });
    qc.invalidateQueries({ queryKey: ["orgTransactions"] });
    if (invoiceId != null) {
      qc.invalidateQueries({ queryKey: invoiceKey(groupId, invoiceId) });
      qc.invalidateQueries({ queryKey: invoiceEventsKey(groupId, invoiceId) });
      qc.invalidateQueries({ queryKey: invoiceReceiptsKey(groupId, invoiceId) });
    }
    qc.invalidateQueries({ queryKey: ["creditSummary", groupId] });
    qc.invalidateQueries({ queryKey: ["creditLots", groupId] });
  };
}

export function useCreateInvoice(groupId: number) {
  const invalidate = useInvoiceInvalidators(groupId);
  return useMutation({
    mutationFn: (payload: CreateInvoicePayload) => createInvoice(groupId, payload),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateInvoice(groupId: number, invoiceId: number) {
  const invalidate = useInvoiceInvalidators(groupId);
  return useMutation({
    mutationFn: (patch: UpdateInvoicePayload) => updateInvoice(groupId, invoiceId, patch),
    onSuccess: () => invalidate(invoiceId),
  });
}

export function useAddInvoiceLine(groupId: number, invoiceId: number) {
  const invalidate = useInvoiceInvalidators(groupId);
  return useMutation({
    mutationFn: (line: AddLinePayload) => addInvoiceLine(groupId, invoiceId, line),
    onSuccess: () => invalidate(invoiceId),
  });
}

export function useRemoveInvoiceLine(groupId: number, invoiceId: number) {
  const invalidate = useInvoiceInvalidators(groupId);
  return useMutation({
    mutationFn: (lineId: number) => removeInvoiceLine(groupId, invoiceId, lineId),
    onSuccess: () => invalidate(invoiceId),
  });
}

export function useFinalizeInvoice(groupId: number, invoiceId: number) {
  const invalidate = useInvoiceInvalidators(groupId);
  return useMutation({
    mutationFn: () => finalizeInvoice(groupId, invoiceId),
    onSuccess: () => invalidate(invoiceId),
  });
}

export function useCancelInvoice(groupId: number, invoiceId: number) {
  const invalidate = useInvoiceInvalidators(groupId);
  return useMutation({
    mutationFn: (reason?: string) => cancelInvoice(groupId, invoiceId, reason),
    onSuccess: () => invalidate(invoiceId),
  });
}

export function useVoidInvoice(groupId: number, invoiceId: number) {
  const invalidate = useInvoiceInvalidators(groupId);
  return useMutation({
    mutationFn: (args: { confirmNumber: string; reason?: string | null }) =>
      voidInvoice(groupId, invoiceId, args.confirmNumber, args.reason),
    onSuccess: () => invalidate(invoiceId),
  });
}

/** Sales pre-provision — release provisional credits before payment (reason required). */
export function useProvisionInvoice(groupId: number, invoiceId: number) {
  const invalidate = useInvoiceInvalidators(groupId);
  return useMutation({
    mutationFn: (reason: string) => provisionInvoice(groupId, invoiceId, reason),
    onSuccess: () => invalidate(invoiceId),
  });
}

/** An invoice's activity log (only fetched when the detail view is open). */
export function useInvoiceEvents(groupId: number, invoiceId: number | null) {
  return useQuery({
    queryKey: ["invoiceEvents", groupId, invoiceId],
    queryFn: () => fetchInvoiceEvents(groupId, invoiceId as number),
    enabled: invoiceId != null,
  });
}

/** Created-by filter options for a group's invoices. */
export function useGroupInvoiceCreators(groupId: number | null | undefined) {
  return useQuery({
    queryKey: ["invoiceCreators", groupId],
    queryFn: () => fetchGroupInvoiceCreators(groupId as number),
    enabled: groupId != null,
  });
}

/** Created-by filter options for the Finance blotter (all orgs; GFG-only endpoint). */
export function useFinanceInvoiceCreators(enabled = true) {
  return useQuery({
    queryKey: ["financeInvoiceCreators"],
    queryFn: fetchFinanceInvoiceCreators,
    enabled,
  });
}

/** The GFG Finance cross-org blotter — server-paginated (org/group/status filters + search). */
export function useFinanceInvoices(
  query: InvoiceListQuery = {},
  // Options, not part of `query` — folding them in would put them in the cache key and send them
  // as request params. `staleTime` lets a picker reuse a recent search instead of refetching it in
  // the background; a mutation's invalidateQueries still refreshes immediately either way.
  opts: { enabled?: boolean; staleTime?: number } = {},
) {
  return useQuery({
    queryKey: financeInvoicesKey(query),
    queryFn: () => fetchFinanceInvoices(query),
    enabled: opts.enabled ?? true,
    staleTime: opts.staleTime,
  });
}

/** The GFG Finance cross-org refunds blotter — server-paginated (search). */
export function useFinanceRefunds(query: RefundListQuery = {}) {
  return useQuery({
    queryKey: ["financeRefunds", query],
    queryFn: () => fetchFinanceRefunds(query),
  });
}

/** Record a client's out-of-band refund (bookkeeping only). Refreshes the Refunds blotter. */
export function useCreateFinanceRefund() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRefundPayload) => createFinanceRefund(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["financeRefunds"] }),
  });
}

/** Reverse a recorded refund. Refreshes the Refunds blotter. */
export function useReverseFinanceRefund() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { refundId: number; payload: ReverseRefundPayload }) =>
      reverseFinanceRefund(args.refundId, args.payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["financeRefunds"] }),
  });
}

/** An invoice's payments that still have refundable balance (New Refund picker). */
export function useRefundablePayments(invoiceId: number | null) {
  return useQuery({
    queryKey: ["refundablePayments", invoiceId],
    queryFn: () => fetchRefundablePayments(invoiceId as number),
    enabled: invoiceId != null,
  });
}

/** Record a payment (full/partial) or write-off against an invoice. */
export function useRecordInvoicePayment(groupId: number, invoiceId: number) {
  const invalidate = useInvoiceInvalidators(groupId);
  return useMutation({
    mutationFn: (payload: RecordPaymentPayload) =>
      recordInvoicePayment(groupId, invoiceId, payload),
    onSuccess: () => invalidate(invoiceId),
  });
}

/** Append-only reversal of a receipt on an invoice, with the credit-lot decision (none/hold/reverse). */
export function useReverseInvoiceReceipt(groupId: number, invoiceId: number) {
  const invalidate = useInvoiceInvalidators(groupId);
  return useMutation({
    mutationFn: (args: {
      receiptId: number;
      reason?: string | null;
      lotAction?: ReverseLotAction;
    }) => reverseInvoiceReceipt(groupId, invoiceId, args.receiptId, args.reason, args.lotAction),
    onSuccess: () => invalidate(invoiceId),
  });
}

/** An invoice's payment receipts (fetched when the detail view is open). */
export function useInvoiceReceipts(groupId: number, invoiceId: number | null) {
  return useQuery({
    queryKey: ["invoiceReceipts", groupId, invoiceId],
    queryFn: () => fetchInvoiceReceipts(groupId, invoiceId as number),
    enabled: invoiceId != null,
  });
}

/** The group's money-in feed (Transactions tab) — server-paginated (+ cash-received subtotal). */
export function useGroupTransactions(
  groupId: number | null | undefined,
  query: ReceiptListQuery = {},
) {
  return useQuery({
    queryKey: ["groupTransactions", groupId, query],
    queryFn: () => fetchGroupTransactions(groupId as number, query),
    enabled: groupId != null,
  });
}

/** An organization's purchases feed across its groups (the org Transactions tab). */
export function useOrgTransactions(orgId: number, query: ReceiptListQuery = {}) {
  return useQuery({
    queryKey: ["orgTransactions", orgId, query],
    queryFn: () => fetchOrgTransactions(orgId, query),
  });
}

/**
 * Records an invoice's Stripe payout by hand. Refreshes the invoice, the finance blotters and the
 * journal entries, since the status, the payout facts and the ledger all move.
 */
export function useRecordInvoicePayout(groupId: number, invoiceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: RecordPayoutPayload) => recordInvoicePayout(invoiceId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoice", groupId, invoiceId] });
      qc.invalidateQueries({ queryKey: ["financeInvoices"] });
      qc.invalidateQueries({ queryKey: ["invoices", groupId] });
      qc.invalidateQueries({ queryKey: ["journalEntries"] });
    },
  });
}
